import { useCallback, useEffect, useRef, useState } from 'react';

import { useOptionalVmClient } from '../api/context';
import type { SessionSummary } from '../api/types';
import { loadFullSessionTimeline } from './chatHistoryTimeline';
import type { TimelineItem } from './useChat';
import { useSessionTitlesStore } from '@/stores/sessionTitles';

export function useChatHistory(opts?: {
  onSessionEvent?: (event: import('../api/types').SessionEvent) => void;
}) {
  const client = useOptionalVmClient();
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [disconnected, setDisconnected] = useState(false);
  const hasConnectedRef = useRef(false);
  const disconnectedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const onSessionEventRef = useRef(opts?.onSessionEvent);
  onSessionEventRef.current = opts?.onSessionEvent;

  const refreshSessions = useCallback(async (q = '') => {
    if (!client) return [];
    setLoading(true);
    try {
      const data = await client.listSessions(50, 0, q);
      setSessions(data);
      hasConnectedRef.current = true;
      setDisconnected(false);
      disconnectedRef.current = false;
      return data;
    } catch {
      if (hasConnectedRef.current) {
        setDisconnected(true);
        disconnectedRef.current = true;
      }
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    if (!client) return;
    const activeClient = client;
    let cancelled = false;

    async function connectSSE() {
      while (!cancelled) {
        const controller = new AbortController();
        abortRef.current = controller;

        try {
          await activeClient.streamSessionEvents(
            (event) => {
              onSessionEventRef.current?.(event);
              if (event.type === 'session_updated') {
                setSessions((prev) => {
                  const idx = prev.findIndex((s) => s.id === event.session.id);
                  if (idx >= 0) {
                    const next = [...prev];
                    next[idx] = event.session;
                    return next;
                  }
                  return [event.session, ...prev];
                });
              } else if (event.type === 'session_deleted') {
                setSessions((prev) => prev.filter((s) => s.id !== event.session_id));
              }
            },
            controller.signal,
            () => {
              // Stream connected — fetch current state. Events arriving via
              // SSE during or after this fetch are applied on top, so no gap.
              void refreshSessions().catch(() => {});
            },
          );
        } catch {
          // stream failed to connect or ended — reconnect
        }

        abortRef.current = null;
        if (!cancelled) {
          await new Promise((r) => setTimeout(r, 1000));
        }
      }
    }

    // On tab foreground: if disconnected, abort current SSE wait to force
    // an immediate reconnection attempt instead of waiting for backoff.
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!disconnectedRef.current) return;
      abortRef.current?.abort();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    void connectSSE();
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      abortRef.current?.abort();
    };
  }, [client, refreshSessions]);

  const removeSession = useCallback(async (sessionId: string) => {
    if (!client) return;
    await client.deleteSession(sessionId);
  }, [client]);

  const renameSession = useCallback(async (sessionId: string, title: string) => {
    if (!client) return;
    try {
      await client.renameSession(sessionId, title);
    } catch {
      // Backend may not support rename yet
    }
  }, [client]);

  const pinSession = useCallback(async (sessionId: string, pinned: boolean) => {
    if (!client) return;
    await client.pinSession(sessionId, pinned);
  }, [client]);

  const stopSession = useCallback(async (sessionId: string) => {
    if (!client) return;
    await client.stopSession(sessionId);
  }, [client]);

  const startSession = useCallback(async (sessionId: string) => {
    if (!client) return;
    await client.startSession(sessionId);
  }, [client]);

  const syncTitles = useSessionTitlesStore((s) => s.sync);
  useEffect(() => {
    if (sessions.length > 0) syncTitles(sessions);
  }, [sessions, syncTitles]);

  const loadSessionTimeline = useCallback(async (sessionId: string): Promise<TimelineItem[]> => {
    if (!client) return [];
    return loadFullSessionTimeline(client, sessionId);
  }, [client]);

  return {
    sessions,
    loading,
    disconnected,
    refreshSessions,
    removeSession,
    renameSession,
    pinSession,
    stopSession,
    startSession,
    loadSessionTimeline,
  };
}
